import { useEffect, useMemo, useState } from "react";

import {
  type EndDateInput,
  getTimeLeftParts,
  HOUR_MS,
  pad2,
  SECOND_MS,
  toValidDate,
} from "./utils";

type TimeLeftParts = ReturnType<typeof getTimeLeftParts>;

const THIRTY_DAYS_MS = 30 * 24 * HOUR_MS;

interface IEventCountDownState {
  dd: string;
  hh: string;
  mm: string;
  ss: string;
  isRed: boolean;
  isMoreThan30Days: boolean;
}

export function useMarketCountDown(
  endDate: EndDateInput
): IEventCountDownState {
  const endDateAsDate = useMemo(() => toValidDate(endDate), [endDate]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const parts = useMemo<TimeLeftParts | null>(() => {
    if (!endDateAsDate) return null;

    void nowMs;
    return getTimeLeftParts(endDateAsDate);
  }, [endDateAsDate, nowMs]);

  useEffect(() => {
    if (!endDateAsDate) return;

    const intervalId = window.setInterval(() => {
      const diffMs = endDateAsDate.getTime() - Date.now();
      setNowMs(Date.now());

      if (diffMs <= 0) window.clearInterval(intervalId);
    }, SECOND_MS);

    return () => window.clearInterval(intervalId);
  }, [endDateAsDate]);

  const diffMs = parts?.diffMs ?? 0;
  const isRed = diffMs < 24 * HOUR_MS;
  const isMoreThan30Days = diffMs > THIRTY_DAYS_MS;

  return {
    dd: parts?.days ? pad2(parts.days) : "",
    hh: pad2(parts?.hours ?? 0),
    mm: pad2(parts?.minutes ?? 0),
    ss: pad2(parts?.seconds ?? 0),
    isRed,
    isMoreThan30Days,
  };
}
